import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import ReportUser from './ReportUser';
import ScheduleSheet from './ScheduleSheet';
import MoreMenu from './MoreMenu';
import { resolveZone } from '../dateUtils';
import { mergeSlots, rangeLabel, profileReady } from '../matchList';
import { displayName, examLabel } from '../names';
import { startCall } from './VideoCall';
import { Icon, Avatar } from './ui';
import { openInviteSheet } from './ShareSheet';
import { examMonthLabel } from '../examMonth';
import { toast } from './feedback';
import { sendFailToast } from '../sendErrors';
import { useBlocked, confirmBlock } from '../blocking';

// "Teaches you Pathology, Pharmacology · You teach Immunology +3"
const summaryLine = m => {
  const list = (xs, n) => xs.slice(0, n).join(', ') + (xs.length > n ? ` +${xs.length - n}` : '');
  return [
    m.canTeach?.length && `Teaches you ${list(m.canTeach, 2)}`,
    m.canLearn?.length && `You teach ${list(m.canLearn, 1)}`,
  ].filter(Boolean).join(' · ');
};

// Matches (brief 018): loaded by Dashboard when the tab opens and kept across tabs.
// Compact cards; tapping one opens the partner sheet with everything.
function FindMatches({ user, userProfile, showNotification, actionsEl, matches, loading, onRefresh, contacts, onFindMatches, onSetup }) {
  const [messageModal, setMessageModal] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [partnerSheet, setPartnerSheet] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [reportModal, setReportModal] = useState(null);
  const blocked = useBlocked(user?.uid);
  const myZone = resolveZone(userProfile?.timezone);

  const sendMessage = async (match) => {
    if (!messageText.trim()) { toast('Please enter a message!', { type: 'error' }); return; }
    try {
      // Same two writes as the chat: the conversation line (both people see it) and the inbox item.
      await addDoc(collection(db,'conversations'),{fromUid:user.uid,fromName:userProfile.name,toUid:match.uid,toName:match.name,content:messageText,participants:[user.uid,match.uid],createdAt:serverTimestamp()});
      await addDoc(collection(db,'messages'),{fromUid:user.uid,fromName:userProfile.name,toUid:match.uid,toName:match.name,content:messageText,read:false,createdAt:serverTimestamp()});
      showNotification('Message sent!',`Your message to ${displayName(match.name)} was delivered.`);
      // Bell entry (and push in the app), same as the chat sends.
      try {
        const recipSnap = await getDoc(doc(db,'users',match.uid));
        if (recipSnap.exists() && recipSnap.data()?.notificationPreferences?.newMessages !== false) {
          await addDoc(collection(db,'notifications'),{
            toUid: match.uid, fromUid: user.uid, fromName: userProfile.name,
            type: 'message',
            text: `${userProfile.name} sent you a message`,
            read: false, createdAt: serverTimestamp()
          });
        }
      } catch(e) { console.warn('notif write failed', e); }
      setMessageText(''); setMessageModal(null);
    } catch(e) { sendFailToast('message', e, 'Error sending message.'); }
  };

  // Blocked people disappear as soon as they're blocked, without searching again.
  const shown = (matches || []).filter(m => !(m.uid in blocked));
  const openMessage = m => { setPartnerSheet(null); setMessageModal(m); setMessageText(''); };
  const openSchedule = m => { setPartnerSheet(null); setScheduleModal({ ...m, name: displayName(m.name) }); };
  const menuItems = m => [
    { label: 'View profile', icon: 'profile', onClick: () => setPartnerSheet(m) },
    { label: 'Report', icon: 'flag', danger: true, onClick: () => setReportModal({ uid: m.uid, name: displayName(m.name) }) },
    { label: 'Block', icon: 'block', danger: true, onClick: () => confirmBlock(user.uid, { uid: m.uid, name: displayName(m.name) }) },
  ];

  if (!profileReady(userProfile)) return (
    <div className="card empty-state">
      <div className="empty-icon"><Icon name="matches" size={40} /></div>
      <h3>Finish your profile</h3>
      <p className="small">Matching needs your exam, the subjects you teach and want to learn, and your free hours.</p>
      <button className="btn-small secondary" style={{ marginTop: '16px' }} onClick={onSetup}>Complete profile</button>
    </div>
  );

  return (
    <>
      <p className="page-sub">{examLabel(userProfile?.studyCategory)}{matches ? ` · ${shown.length} match${shown.length===1?'':'es'}` : ''}{loading && matches ? ' · Refreshing…' : ''}</p>
      {actionsEl && createPortal(
        <button className="btn-icon small plain" onClick={onRefresh} disabled={loading} aria-label="Refresh matches"><Icon name="refresh" /></button>,
        actionsEl
      )}
      {matches === null ? (
        <div className="matches-container" aria-busy="true" aria-label="Loading matches">
          {[0, 1, 2].map(i => <div key={i} className="match-card skeleton-card" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-icon"><Icon name="matches" size={40} /></div>
          {/* P-1: a way forward instead of a dead end. */}
          <h3>You're early, and that's fixable</h3>
          <p className="small">Nobody in {examLabel(userProfile?.studyCategory) || 'your exam'} fits your week yet. Invite someone you'd like to study with, and you'll be connected as soon as they join.</p>
          <div className="empty-actions">
            <button className="btn-small" onClick={() => openInviteSheet(user.uid, userProfile)}>Invite a study partner</button>
            <button className="btn-small secondary" onClick={onFindMatches}>Edit profile</button>
          </div>
        </div>
      ) : (
        <div className="matches-container">
          {shown.map(m => {
            const name = displayName(m.name);
            const free = mergeSlots(m.commonSlots || []);
            const langs = (m.sharedLangs || []).filter(l => l !== 'English');
            return (
              <div key={m.uid} className="match-card compact" onClick={() => setPartnerSheet(m)}>
                <div className="match-header-with-pic">
                  <Avatar src={m.profilePicture} name={name} size={56} viewable />
                  <button type="button" className="match-info match-open" onClick={e => { e.stopPropagation(); setPartnerSheet(m); }} aria-label={`View ${name}'s profile`}>
                    <span className="match-name">{name}</span>
                    {(m.school || examMonthLabel(m.examMonth)) && <span className="match-meta">{[m.school, examMonthLabel(m.examMonth)].filter(Boolean).join(' · ')}</span>}
                  </button>
                  <div className="match-score"><div className="match-score-value">{m.score}%</div><div className="match-score-label">MATCH</div></div>
                  <span className="match-more" onClick={e => e.stopPropagation()}><MoreMenu label={`More options for ${name}`} items={menuItems(m)} /></span>
                </div>
                {summaryLine(m) && <p className="match-summary">{summaryLine(m)}</p>}
                {langs.length > 0 && <div className="match-langs">{langs.map(l => <span key={l} className="chip accent">{l}</span>)}</div>}
                {free.length > 0 && (
                  <div className="match-free"><Icon name="calendar" size={20} /><div><strong>Free together</strong>{free.slice(0, 2).map(r => rangeLabel(r, myZone)).join(', ')}{free.length > 2 && <span className="more"> +{free.length - 2} more</span>}</div></div>
                )}
                <div className="match-actions" onClick={e => e.stopPropagation()}>
                  <button className="btn-small accent-outline" onClick={() => openSchedule(m)}>Schedule</button>
                  <button className="btn-icon small" aria-label={`Message ${name}`} onClick={() => openMessage(m)}><Icon name="messages" /></button>
                  {contacts.has(m.uid) && <button className="btn-icon small" aria-label={`Video call with ${name}`} onClick={() => startCall(m.uid, name)}><Icon name="video" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {partnerSheet && (() => {
        const m = partnerSheet;
        const name = displayName(m.name);
        const free = mergeSlots(m.commonSlots || []);
        const langs = m.languages || [];
        const meta = [m.age && `${m.age} yrs`, m.gender, [m.city, m.country].filter(Boolean).join(', ')].filter(Boolean);
        return (
          <div className="modal-overlay" onClick={() => setPartnerSheet(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-label={name}>
              <div className="modal-head">
                <h3>{name}</h3>
                <button className="modal-close" onClick={() => setPartnerSheet(null)} aria-label="Close">×</button>
              </div>
              <div className="partner-top">
                <Avatar src={m.profilePicture} name={name} size={72} viewable />
                <div className="match-info">
                  {meta.length > 0 && <div className="match-meta">{meta.join(' · ')}</div>}
                  {(m.school || m.yearLevel) && <div className="match-meta">{[m.school, m.yearLevel].filter(Boolean).join(' — ')}</div>}
                  {examMonthLabel(m.examMonth) && <div className="match-meta">{examMonthLabel(m.examMonth)}</div>}
                </div>
                <div className="match-score"><div className="match-score-value">{m.score}%</div><div className="match-score-label">MATCH</div></div>
              </div>
              <div className="match-details">
                {m.canTeach?.length > 0 && <div className="match-section"><div className="section-label">Can teach you</div>{m.canTeach.map(x => <span key={x} className="chip">{x}</span>)}</div>}
                {m.canLearn?.length > 0 && <div className="match-section"><div className="section-label">Wants to learn from you</div>{m.canLearn.map(x => <span key={x} className="chip">{x}</span>)}</div>}
                {langs.length > 0 && <div className="match-section"><div className="section-label">Languages</div>{langs.map(l => <span key={l} className={`chip ${(m.sharedLangs || []).includes(l) ? 'accent' : ''}`}>{l}</span>)}</div>}
                <div className="match-section">
                  <div className="section-label">Free together (your time)</div>
                  {free.length ? <ul className="partner-free">{free.map(r => <li key={r.startMin}>{rangeLabel(r, myZone)}</li>)}</ul> : <p className="small muted">No shared hours yet. Send a message to find a time.</p>}
                </div>
              </div>
              <div className="partner-actions">
                <button className="btn" onClick={() => openSchedule(m)}>Schedule a session</button>
                <button className="btn secondary" onClick={() => openMessage(m)}>Message</button>
              </div>
            </div>
          </div>
        );
      })()}

      {messageModal&&(
        <div className="modal-overlay" onClick={()=>setMessageModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Message {displayName(messageModal.name)}</h3>
              <button className="modal-close" onClick={()=>setMessageModal(null)} aria-label="Close">×</button>
            </div>
            <textarea className="message-box" placeholder={`Hi ${displayName(messageModal.name)}! I'd love to study together...`} value={messageText} onChange={e=>setMessageText(e.target.value)} />
            <button className="btn full" onClick={()=>sendMessage(messageModal)} disabled={!messageText.trim()}>Send Message</button>
          </div>
        </div>
      )}

      {scheduleModal&&(
        <ScheduleSheet user={user} userProfile={userProfile} partner={scheduleModal} onClose={()=>setScheduleModal(null)} />
      )}
      {reportModal && (
        <ReportUser
          user={user}
          userProfile={userProfile}
          reported={reportModal}
          onClose={()=>setReportModal(null)}
          showNotification={showNotification}
        />
      )}
    </>
  );
}
export default FindMatches;
